// Extracted from original AccountTab (unchanged logic, isolated file).
// Profile (name/email/password), 2FA and backup codes.
import { useState, useEffect } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  Save, UserCog, Mail, Lock, Eye, EyeOff, Check, AlertTriangle, Shield, Copy, LogOut,
} from "lucide-react";

export default function CompteSection() {
  const { logoutAllSessions } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaDisablePw, setTwoFaDisablePw] = useState("");
  const [twoFaRegenPw, setTwoFaRegenPw] = useState("");
  const [twoFaBackupCount, setTwoFaBackupCount] = useState(null);
  const [showBackupCodes, setShowBackupCodes] = useState(null);

  useEffect(() => { loadProfile(); load2faStatus(); }, []);

  const loadProfile = async () => {
    try { const r = await api.get("/auth/profile"); setProfile({ name: r.data.name || "", email: r.data.email || "" }); }
    catch { /* silent */ }
  };
  const load2faStatus = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setTwoFaEnabled(r.data.enabled);
      setTwoFaBackupCount(r.data.backup_codes_remaining ?? null);
    } catch { /* silent */ }
  };

  const start2faSetup = async () => {
    try { const r = await api.post("/auth/2fa/setup"); setTwoFaSetup(r.data); setTwoFaCode(""); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const confirm2fa = async () => {
    try {
      const r = await api.post("/auth/2fa/confirm", { totp_code: twoFaCode });
      setShowBackupCodes(r.data.backup_codes);
      setTwoFaEnabled(true); setTwoFaSetup(null); setTwoFaCode("");
      toast.success("2FA active !");
    } catch (err) { toast.error(err.response?.data?.detail || "Code invalide"); }
  };
  const disable2fa = async () => {
    try {
      await api.post("/auth/2fa/disable", { password: twoFaDisablePw });
      setTwoFaEnabled(false); setTwoFaDisablePw(""); setTwoFaBackupCount(null);
      toast.success("2FA desactive");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const regenerateBackupCodes = async () => {
    try {
      const r = await api.post("/auth/2fa/regenerate-backup-codes", { password: twoFaRegenPw });
      setShowBackupCodes(r.data.backup_codes);
      setTwoFaRegenPw(""); setTwoFaBackupCount(r.data.backup_codes.length);
      toast.success("Nouveaux codes de secours generes");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const persistToken = (res) => {
    if (res.data.access_token) {
      localStorage.setItem("token", res.data.access_token);
      api.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    }
  };

  const handleSaveName = async () => {
    if (!pw.current_password) { toast.error("Mot de passe actuel requis"); return; }
    setSaving(true);
    try {
      const res = await api.put("/auth/update-profile", { name: profile.name, current_password: pw.current_password });
      toast.success("Nom mis a jour"); persistToken(res);
      setPw(p => ({ ...p, current_password: "" }));
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleSaveEmail = async () => {
    if (!pw.current_password) { toast.error("Mot de passe actuel requis"); return; }
    if (!profile.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      toast.error("Format de courriel invalide"); return;
    }
    setSaving(true);
    try {
      const res = await api.put("/auth/update-profile", { email: profile.email, current_password: pw.current_password });
      toast.success("Courriel mis a jour."); persistToken(res);
      setPw(p => ({ ...p, current_password: "" }));
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleSavePassword = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Mots de passe requis"); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (pw.new_password.length < 8) { toast.error("Minimum 8 caracteres"); return; }
    setSaving(true);
    try {
      const res = await api.put("/auth/update-profile", {
        new_password: pw.new_password, confirm_password: pw.confirm_password, current_password: pw.current_password,
      });
      toast.success("Mot de passe modifie"); persistToken(res);
      setPw({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const pwStrength = (p) => {
    if (!p) return null;
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 2) return { label: "Faible", color: "text-red-500", bg: "bg-red-500", w: "w-1/4" };
    if (score <= 3) return { label: "Moyen", color: "text-yellow-500", bg: "bg-yellow-500", w: "w-2/4" };
    if (score <= 4) return { label: "Fort", color: "text-blue-500", bg: "bg-blue-500", w: "w-3/4" };
    return { label: "Excellent", color: "text-green-500", bg: "bg-green-500", w: "w-full" };
  };
  const strength = pwStrength(pw.new_password);

  return (
    <div className="space-y-5" data-testid="account-tab">
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <p className="text-sm font-medium text-yellow-800 flex items-center gap-2 mb-2">
          <Lock className="w-4 h-4" /> Mot de passe actuel requis pour toute modification
        </p>
        <div className="relative">
          <input type={showCurrent ? "text" : "password"} value={pw.current_password}
            onChange={e => setPw({ ...pw, current_password: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 pr-10 text-sm" placeholder="Votre mot de passe actuel"
            data-testid="current-password-input" />
          <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
          <UserCog className="w-4 h-4 text-blue-600" /> Nom d'utilisateur
        </h3>
        <div className="flex gap-2">
          <input type="text" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })}
            className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="Nom complet" data-testid="profile-name-input" />
          <button onClick={handleSaveName} disabled={saving || !pw.current_password || !profile.name}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="save-name-btn">
            <Save className="w-3.5 h-3.5" /> Modifier
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4 text-blue-600" /> Courriel du compte
        </h3>
        <p className="text-xs text-gray-500 mb-3">L'ancien courriel sera completement libere et detache du compte.</p>
        <div className="flex gap-2">
          <input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })}
            className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="courriel@exemple.com" data-testid="profile-email-input" />
          <button onClick={handleSaveEmail} disabled={saving || !pw.current_password || !profile.email}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="save-email-btn">
            <Save className="w-3.5 h-3.5" /> Modifier
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
          <Lock className="w-4 h-4 text-blue-600" /> Modifier le mot de passe
        </h3>
        <div className="space-y-3">
          <div className="relative">
            <input type={showNew ? "text" : "password"} value={pw.new_password}
              onChange={e => setPw({ ...pw, new_password: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 pr-10 text-sm" placeholder="Nouveau mot de passe"
              data-testid="new-password-input" />
            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {strength && (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${strength.bg} ${strength.w}`} /></div>
              <span className={`text-xs font-medium ${strength.color}`}>{strength.label}</span>
            </div>
          )}
          <input type="password" value={pw.confirm_password}
            onChange={e => setPw({ ...pw, confirm_password: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Confirmer"
            data-testid="confirm-password-input" />
          {pw.new_password && pw.confirm_password && pw.new_password !== pw.confirm_password && (
            <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Ne correspondent pas</p>
          )}
          {pw.new_password && pw.confirm_password && pw.new_password === pw.confirm_password && (
            <p className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> Correspondent</p>
          )}
          <button onClick={handleSavePassword}
            disabled={saving || !pw.current_password || !pw.new_password || pw.new_password !== pw.confirm_password}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="save-password-btn">
            <Lock className="w-3.5 h-3.5" /> Modifier le mot de passe
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5" data-testid="sessions-section">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
          <LogOut className="w-4 h-4 text-blue-600" /> Sessions actives
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Déconnecte ce compte sur tous les appareils et navigateurs (les jetons existants sont révoqués). À utiliser si vous pensez qu'une session a été compromise.
        </p>
        <button onClick={async () => { if (!window.confirm("Déconnecter toutes les sessions, y compris celle-ci ?")) return; try { await logoutAllSessions(); window.location.href = "/admin/login"; } catch { toast.error("Impossible de révoquer les sessions"); } }}
          className="border border-rose-200 text-rose-700 px-4 py-2 rounded-lg text-sm hover:bg-rose-50 flex items-center gap-2" data-testid="logout-all-sessions-btn">
          <LogOut className="w-3.5 h-3.5" /> Déconnecter toutes les sessions
        </button>
      </div>

      <div className="bg-white border rounded-xl p-5" data-testid="2fa-section">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-blue-600" /> Authentification a deux facteurs (2FA)
        </h3>
        {twoFaEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <Check className="w-3 h-3" /> 2FA active
              </span>
              {twoFaBackupCount !== null && (
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${twoFaBackupCount <= 2 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}
                  data-testid="backup-codes-count">
                  {twoFaBackupCount} code(s) de secours
                </span>
              )}
            </div>
            <div className="border rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-2">Regenerer les codes de secours :</p>
              <div className="flex gap-2">
                <input type="password" value={twoFaRegenPw} onChange={e => setTwoFaRegenPw(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="Mot de passe actuel" data-testid="2fa-regen-password" />
                <button onClick={regenerateBackupCodes} disabled={!twoFaRegenPw}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 disabled:opacity-50" data-testid="2fa-regen-btn">
                  Regenerer
                </button>
              </div>
            </div>
            <div className="border border-red-100 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-2">Desactiver le 2FA :</p>
              <div className="flex gap-2">
                <input type="password" value={twoFaDisablePw} onChange={e => setTwoFaDisablePw(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="Mot de passe actuel" data-testid="2fa-disable-password" />
                <button onClick={disable2fa} disabled={!twoFaDisablePw}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-xs hover:bg-red-700 disabled:opacity-50" data-testid="2fa-disable-btn">
                  Desactiver
                </button>
              </div>
            </div>
          </div>
        ) : twoFaSetup ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Scannez ce code dans votre app d'authentification :</p>
            <div className="bg-gray-50 border rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 mb-2">Cle secrete :</p>
              <div className="flex items-center justify-center gap-2">
                <code className="text-sm font-mono bg-white px-3 py-1 rounded border select-all">{twoFaSetup.secret}</code>
                <button onClick={() => { navigator.clipboard.writeText(twoFaSetup.secret); toast.success("Copie!"); }}
                  className="p-1 text-gray-400 hover:text-blue-600"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Code :</label>
              <div className="flex gap-2">
                <input type="text" value={twoFaCode} onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="border rounded-lg px-3 py-2 text-sm font-mono text-center tracking-widest w-40" placeholder="000000" maxLength={6}
                  data-testid="2fa-confirm-code" />
                <button onClick={confirm2fa} disabled={twoFaCode.length !== 6}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50" data-testid="2fa-confirm-btn">
                  Activer
                </button>
              </div>
            </div>
            <button onClick={() => setTwoFaSetup(null)} className="text-xs text-gray-400">Annuler</button>
          </div>
        ) : (
          <div>
            <p className="text-xs text-gray-500 mb-3">Ajoutez un code temporaire genere par votre telephone.</p>
            <button onClick={start2faSetup} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2" data-testid="2fa-setup-btn">
              <Shield className="w-3.5 h-3.5" /> Configurer le 2FA
            </button>
          </div>
        )}
        {showBackupCodes && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Codes de secours (a sauvegarder)
            </h4>
            <p className="text-xs text-yellow-700 mb-2">A usage unique. Conservez-les en lieu sur.</p>
            <div className="grid grid-cols-4 gap-1">
              {showBackupCodes.map((code, i) => (
                <code key={i} className="text-xs font-mono bg-white px-2 py-1 rounded border text-center">{code}</code>
              ))}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(showBackupCodes.join("\n")); toast.success("Codes copies!"); }}
              className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copier
            </button>
            <button onClick={() => setShowBackupCodes(null)} className="ml-4 mt-2 text-xs text-gray-400">Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}
